<template>
    <div id="frontline-dashboard-mobile">
        <div class="safe-area-inset-top" />

        <TransitionGroup name="dashboard-layout">
            <div key="fm-dash-header" class="section section--header">
                <h1 class="welcome-message">{{ welcomeMessage(userName) }}</h1>

                <div class="dashboard-filter-bar">
                    <FilterBarTime
                        v-model="timeFilter"
                        :disabled="isPageLoading"
                        data-testid="fm-dash-time-filter"
                        @filter-changed="onFilterChange"
                    />

                    <FilterBarQuestionType
                        v-model="metricFilter"
                        :disabled="isPageLoading"
                        :active-metrics="activeMetrics"
                        :is-multi-metric="
                            !!companyVars?.has_lead_with_any_question
                        "
                        @filter-changed="onFilterChange"
                    />
                </div>
            </div>

            <div key="fm-dash-responses" class="section section--row">
                <UserFocusArea
                    :on-reload-click="fetchUserFocusArea"
                    :focus-area="userFocusArea"
                    :on-action-acknowledge="acknowledgeFocusAreaAction"
                    :loading="loadingUserFocusArea"
                    :show-error="hasErrorUserFocusArea"
                ></UserFocusArea>
            </div>
        </TransitionGroup>
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { Style } from '@capacitor/status-bar'
import { setStatusBarStyle } from '@/mobile/src/utils/capacitor'
import { useFrontlineManagerDash } from '@/composables/useFrontlineManagerDash'
import { useFrontlineStaffDash } from '@/composables/useFrontlineStaffDash'
import UserFocusArea from '@/components/FrontlineStaffDash/UserFocusArea.vue'
import FilterBarTime from '@/components/PrimeVue/FilterBarTime.vue'
import FilterBarQuestionType from '@/components/PrimeVue/FilterBarQuestionType.vue'
import { TimeOptionLabel } from '@/utils/time'

const store = useStore()

const {
    timeFilter,
    metricFilter,
    welcomeMessage,
    setTimeFilter,
    saveTimeFilter,
} = useFrontlineManagerDash()

const {
    loadingUserFocusArea,
    hasErrorUserFocusArea,
    userFocusArea,
    isPageLoading,
    fetchPageData,
    fetchUserFocusArea,
    acknowledgeFocusAreaAction,
} = useFrontlineStaffDash()

const userName = computed(() => store.getters['userFirstName'])
const companyVars = computed(() => store.getters['$companyVars'])
const activeMetrics = computed(() => store.getters['activeMetrics'])

const savedTimeFilter = computed(
    () => store.getters['getCopilotDashboardFilters']
)

onBeforeMount(async () => {
    await store.dispatch('getFilters') //Make sure the user lock filters are loaded
    metricFilter.value = store.getters.filterQuestionType ?? 'nps'

    if (savedTimeFilter.value) {
        setTimeFilter(savedTimeFilter.value)
    }

    await fetchPageData(companyVars.value.has_team_goal)
})

onMounted(async () => {
    await setStatusBarStyle(Style.Light)
})

onUnmounted(async () => {
    await setStatusBarStyle(Style.Dark)
})

const onFilterChange = async (timeOption?: TimeOptionLabel) => {
    if (timeOption) {
        saveTimeFilter(timeOption)
    }

    await fetchPageData(companyVars.value.has_team_goal)
}
</script>

<style lang="less" scoped>
@import '~@/mobile/src/styles/dashboardMobile.less';
</style>
